import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  CameraOff,
  GripVertical,
  LoaderCircle,
  Maximize2,
  Minus,
  PictureInPicture2,
  Plus,
  RotateCcw,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import type { CameraPreviewState } from './useRobotCameraPreview';

export const CAMERA_STREAM_WAIT_TIMEOUT_MS = 5000;
export const CAMERA_PREVIEW_MIN_WIDTH = 280;
export const CAMERA_PREVIEW_MAX_WIDTH = 720;
export const CAMERA_PREVIEW_SCALE_STEP = 80;
const CAMERA_PREVIEW_VIEWPORT_MARGIN = 20;
const CAMERA_PREVIEW_HEADER_HEIGHT = 51;

export type CameraFloatingLayout = {
  position: { left: number; top: number } | null;
  width: number | null;
};

type RobotCameraPreviewProps = {
  state: CameraPreviewState;
  stream: MediaStream | null;
  robotName: string;
  variant?: 'floating' | 'docked';
  rotated?: boolean;
  // 埋め込み⇄フローティングの切替でコンポーネントが再マウントされるため、
  // 消音状態とフローティングの配置は親が保持し、マウント時の初期値として受け取る
  defaultAudioMuted?: boolean;
  defaultFloatingLayout?: CameraFloatingLayout;
  // 埋め込みレイヤーが非表示（最小化・3Dシミュレーション中）の間だけ音声を止める
  suspendAudio?: boolean;
  onAudioMutedChange?: (muted: boolean) => void;
  onFloatingLayoutChange?: (layout: CameraFloatingLayout) => void;
  onDock?: () => void;
  onExpand?: () => void;
  onClose: () => void;
  onRetry: () => void;
  onPlaying: (robotId: string) => void;
  onWaiting: (robotId: string) => void;
  onError: (robotId: string, message: string) => void;
};

const STATUS_LABELS: Record<CameraPreviewState['status'], string> = {
  off: 'カメラOFF',
  starting: 'カメラ開始中',
  'waiting-stream': '映像を待っています',
  playing: '映像受信中',
  switching: 'カメラ切替中',
  stopping: 'カメラ終了中',
  error: 'カメラエラー',
};

// eslint-disable-next-line react-refresh/only-export-components
export const hasUsableCameraTrack = (stream: MediaStream | null) => Boolean(
  stream?.getVideoTracks().some((track) => track.readyState === 'live' && !track.muted),
);

// eslint-disable-next-line react-refresh/only-export-components
export const hasUsableAudioTrack = (stream: MediaStream | null) => Boolean(
  stream?.getAudioTracks().some((track) => track.readyState === 'live' && !track.muted),
);

type PreviewPosition = {
  left: number;
  top: number;
};

type PreviewDrag = {
  pointerId: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
};

type RobotCameraMediaProps = RobotCameraPreviewProps & {
  robotId: string;
  variant: 'floating' | 'docked';
  onStartDragging?: (event: ReactPointerEvent<HTMLElement>) => void;
  onMovePreview?: (event: ReactPointerEvent<HTMLElement>) => void;
  onStopDragging?: (event: ReactPointerEvent<HTMLElement>) => void;
  onScalePreview: (direction: -1 | 1) => void;
};

const getPreviewWidthBounds = () => {
  const availableByWidth = Math.max(1, window.innerWidth - CAMERA_PREVIEW_VIEWPORT_MARGIN);
  const availableByHeight = Math.max(
    1,
    (window.innerHeight - CAMERA_PREVIEW_VIEWPORT_MARGIN - CAMERA_PREVIEW_HEADER_HEIGHT) * 4 / 3,
  );
  const availableWidth = Math.min(availableByWidth, availableByHeight);
  const minimumWidth = Math.min(CAMERA_PREVIEW_MIN_WIDTH, availableWidth);
  return {
    minimumWidth,
    maximumWidth: Math.max(minimumWidth, Math.min(CAMERA_PREVIEW_MAX_WIDTH, availableWidth)),
  };
};

function RobotCameraMedia({
  state,
  stream,
  robotName,
  robotId,
  variant,
  rotated = false,
  defaultAudioMuted = false,
  suspendAudio = false,
  onAudioMutedChange,
  onDock,
  onExpand,
  onClose,
  onRetry,
  onPlaying,
  onWaiting,
  onError,
  onStartDragging,
  onMovePreview,
  onStopDragging,
  onScalePreview,
}: RobotCameraMediaProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const frameConfirmedRef = useRef(false);
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [audioMuted, setAudioMuted] = useState(defaultAudioMuted);
  const [audioRequiresAction, setAudioRequiresAction] = useState(false);
  const { status } = state;
  const confirmPlayback = () => {
    frameConfirmedRef.current = true;
    onPlaying(robotId);
  };

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (!audio || !audioAvailable) return;
    if (audioMuted || audioRequiresAction) {
      audio.muted = false;
      setAudioMuted(false);
      onAudioMutedChange?.(false);
      void audio.play()
        .then(() => setAudioRequiresAction(false))
        .catch(() => setAudioRequiresAction(true));
      return;
    }
    audio.muted = true;
    audio.pause();
    setAudioMuted(true);
    onAudioMutedChange?.(true);
    setAudioRequiresAction(false);
  };

  useEffect(() => {
    const video = videoRef.current;
    frameConfirmedRef.current = false;
    if (!video) return;
    video.srcObject = stream;
    if (stream) {
      void video.play().catch(() => {
        if (robotId) onError(robotId, '映像を再生できませんでした。再試行してください。');
      });
    }
    return () => {
      video.pause();
      video.srcObject = null;
      frameConfirmedRef.current = false;
    };
  }, [onError, robotId, state.leaseToken, stream]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.srcObject = stream;
    const cleanups = new Map<MediaStreamTrack, () => void>();
    const evaluateAudio = () => {
      const currentTracks = new Set(stream?.getAudioTracks() ?? []);
      cleanups.forEach((cleanup, track) => {
        if (currentTracks.has(track)) return;
        cleanup();
        cleanups.delete(track);
      });
      currentTracks.forEach((track) => {
        if (cleanups.has(track)) return;
        track.addEventListener('mute', evaluateAudio);
        track.addEventListener('unmute', evaluateAudio);
        track.addEventListener('ended', evaluateAudio);
        cleanups.set(track, () => {
          track.removeEventListener('mute', evaluateAudio);
          track.removeEventListener('unmute', evaluateAudio);
          track.removeEventListener('ended', evaluateAudio);
        });
      });

      const available = hasUsableAudioTrack(stream);
      setAudioAvailable(available);
      const muted = audioMuted || suspendAudio;
      audio.muted = muted;
      if (!available || muted) {
        audio.pause();
        setAudioRequiresAction(false);
        return;
      }
      void audio.play()
        .then(() => setAudioRequiresAction(false))
        .catch(() => setAudioRequiresAction(true));
    };
    stream?.addEventListener('addtrack', evaluateAudio);
    stream?.addEventListener('removetrack', evaluateAudio);
    evaluateAudio();
    return () => {
      stream?.removeEventListener('addtrack', evaluateAudio);
      stream?.removeEventListener('removetrack', evaluateAudio);
      cleanups.forEach((cleanup) => cleanup());
      cleanups.clear();
      audio.pause();
      audio.srcObject = null;
    };
  }, [audioMuted, state.leaseToken, stream, suspendAudio]);

  useEffect(() => {
    if (!stream || !robotId) return;
    const video = videoRef.current;
    const cleanups = new Map<MediaStreamTrack, () => void>();

    const evaluateTracks = () => {
      const currentTracks = new Set(stream.getVideoTracks());
      cleanups.forEach((cleanup, track) => {
        if (currentTracks.has(track)) return;
        cleanup();
        cleanups.delete(track);
      });
      currentTracks.forEach((track) => {
        if (cleanups.has(track)) return;
        const onUnavailable = () => {
          frameConfirmedRef.current = false;
          onWaiting(robotId);
        };
        const onAvailable = () => {
          if (track.readyState !== 'live' || track.muted) return;
          void video?.play().catch(() => onError(robotId, '映像を再生できませんでした。再試行してください。'));
        };
        track.addEventListener('mute', onUnavailable);
        track.addEventListener('ended', onUnavailable);
        track.addEventListener('unmute', onAvailable);
        cleanups.set(track, () => {
          track.removeEventListener('mute', onUnavailable);
          track.removeEventListener('ended', onUnavailable);
          track.removeEventListener('unmute', onAvailable);
        });
      });

      if (!hasUsableCameraTrack(stream)) {
        frameConfirmedRef.current = false;
        onWaiting(robotId);
        return;
      }
      void video?.play().catch(() => onError(robotId, '映像を再生できませんでした。再試行してください。'));
    };

    stream.addEventListener('addtrack', evaluateTracks);
    stream.addEventListener('removetrack', evaluateTracks);
    evaluateTracks();
    return () => {
      stream.removeEventListener('addtrack', evaluateTracks);
      stream.removeEventListener('removetrack', evaluateTracks);
      cleanups.forEach((cleanup) => cleanup());
      cleanups.clear();
    };
  }, [onError, onWaiting, robotId, stream]);

  useEffect(() => {
    if (status !== 'waiting-stream' || !robotId) return;
    if (frameConfirmedRef.current) {
      onPlaying(robotId);
      return;
    }
    const timeout = window.setTimeout(() => {
      onError(robotId, 'ロボット映像を受信できません。接続を確認して再試行してください。');
    }, CAMERA_STREAM_WAIT_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [onError, onPlaying, robotId, status, stream]);

  const busy = status === 'starting' || status === 'switching' || status === 'stopping';
  const showWaiting = status === 'waiting-stream' || busy;
  const audioButton = (
    <button
      type="button"
      className={`robot-camera-preview__icon-button${!audioMuted && !audioRequiresAction && audioAvailable ? ' is-active' : ''}`}
      aria-label={audioMuted
        ? 'ロボット音声をONにする'
        : audioRequiresAction
          ? 'ロボット音声を再生'
          : 'ロボット音声を消音'}
      title={audioAvailable ? 'ロボットのマイク音声を切り替える' : 'ロボット音声を待っています'}
      disabled={!audioAvailable || status === 'stopping'}
      onClick={toggleAudio}
    >
      {audioMuted ? <VolumeX size={17} /> : <Volume2 size={17} />}
    </button>
  );

  return (
    <>
      {variant === 'floating' && (
        <header
          className="robot-camera-preview__header"
          title="ドラッグしてカメラ映像を移動"
          onPointerDown={onStartDragging}
          onPointerMove={onMovePreview}
          onPointerUp={onStopDragging}
          onPointerCancel={onStopDragging}
        >
          <div>
            <GripVertical className="robot-camera-preview__drag-indicator" size={17} aria-hidden="true" />
            <strong>{robotName}</strong>
            <span className={`robot-camera-preview__status robot-camera-preview__status--${status}`}>
              {STATUS_LABELS[status]}
            </span>
          </div>
          <div className="robot-camera-preview__actions">
            {audioButton}
            {onDock && (
              <button
                type="button"
                className="robot-camera-preview__icon-button"
                aria-label="カメラ映像を埋め込み表示に戻す"
                title="3Dロボット枠へ戻す"
                onClick={onDock}
              >
                <PictureInPicture2 size={17} />
              </button>
            )}
            <button
              type="button"
              className="robot-camera-preview__icon-button"
              aria-label="カメラ映像を縮小"
              title="カメラ映像を縮小"
              onClick={() => onScalePreview(-1)}
            >
              <Minus size={17} />
            </button>
            <button
              type="button"
              className="robot-camera-preview__icon-button"
              aria-label="カメラ映像を拡大"
              title="カメラ映像を拡大"
              onClick={() => onScalePreview(1)}
            >
              <Plus size={17} />
            </button>
            <button
              type="button"
              className="robot-camera-preview__icon-button robot-camera-preview__icon-button--close"
              aria-label="カメラ映像を閉じる"
              title="カメラをOFFにする"
              disabled={status === 'stopping'}
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </div>
        </header>
      )}

      <div className={`robot-camera-preview__video-wrap${rotated ? ' is-rotated' : ''}`}>
        <video
          ref={videoRef}
          className="robot-camera-preview__video"
          autoPlay
          playsInline
          muted
          onLoadedMetadata={(event) => {
            void event.currentTarget.play().catch(() => onError(robotId, '映像を再生できませんでした。再試行してください。'));
          }}
          onPlaying={() => {
            confirmPlayback();
          }}
          onTimeUpdate={confirmPlayback}
          onError={() => onError(robotId, '映像の再生中にエラーが発生しました。再試行してください。')}
        />
        <audio ref={audioRef} autoPlay muted={audioMuted || suspendAudio} aria-hidden="true" />

        {showWaiting && (
          <div className="robot-camera-preview__message" role="status">
            <LoaderCircle className="animate-spin" size={24} />
            <span>{STATUS_LABELS[status]}</span>
          </div>
        )}
        {status === 'error' && (
          <div className="robot-camera-preview__message robot-camera-preview__message--error" role="alert">
            <CameraOff size={25} />
            <span>{state.errorMessage ?? '映像を表示できませんでした'}</span>
            <button type="button" onClick={onRetry}>
              <RotateCcw size={15} />
              再試行
            </button>
          </div>
        )}

        {variant === 'docked' && (
          <>
            <div className="robot-camera-preview__docked-actions">
              {audioButton}
              {onExpand && (
                <button
                  type="button"
                  className="robot-camera-preview__icon-button"
                  aria-label="カメラ映像をフローティング表示に切り替える"
                  title="カメラ映像を拡大表示する"
                  onClick={onExpand}
                >
                  <Maximize2 size={17} />
                </button>
              )}
              <button
                type="button"
                className="robot-camera-preview__icon-button robot-camera-preview__icon-button--close"
                aria-label="カメラ映像を閉じる"
                title="カメラをOFFにする"
                disabled={status === 'stopping'}
                onClick={onClose}
              >
                <X size={18} />
              </button>
            </div>
            <div className={`robot-camera-preview__docked-info${status === 'playing' ? ' is-playing' : ''}`}>
              <strong>{robotName}</strong>
              {status !== 'playing' && (
                <span className={`robot-camera-preview__status robot-camera-preview__status--${status}`}>
                  {STATUS_LABELS[status]}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export function RobotCameraPreview({
  state,
  stream,
  robotName,
  variant = 'floating',
  rotated = false,
  defaultAudioMuted,
  defaultFloatingLayout,
  suspendAudio,
  onAudioMutedChange,
  onFloatingLayoutChange,
  onDock,
  onExpand,
  onClose,
  onRetry,
  onPlaying,
  onWaiting,
  onError,
}: RobotCameraPreviewProps) {
  const previewRef = useRef<HTMLElement>(null);
  const dragRef = useRef<PreviewDrag | null>(null);
  const [position, setPosition] = useState<PreviewPosition | null>(defaultFloatingLayout?.position ?? null);
  const [previewWidth, setPreviewWidth] = useState<number | null>(defaultFloatingLayout?.width ?? null);
  const [dragging, setDragging] = useState(false);
  const { robotId, status } = state;

  const constrainPosition = useCallback((next: PreviewPosition, width: number, height: number) => ({
    left: Math.min(Math.max(0, next.left), Math.max(0, window.innerWidth - width)),
    top: Math.min(Math.max(0, next.top), Math.max(0, window.innerHeight - height)),
  }), []);

  const startDragging = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;
    const preview = previewRef.current;
    if (!preview) return;
    const rect = preview.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    event.preventDefault();
  };

  const movePreview = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPosition(constrainPosition({
      left: event.clientX - drag.offsetX,
      top: event.clientY - drag.offsetY,
    }, drag.width, drag.height));
    event.preventDefault();
  };

  const stopDragging = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragging(false);
    onFloatingLayoutChange?.({ position, width: previewWidth });
  };

  const scalePreview = (direction: -1 | 1) => {
    const preview = previewRef.current;
    if (!preview) return;
    const { minimumWidth, maximumWidth } = getPreviewWidthBounds();
    const nextWidth = Math.min(
      maximumWidth,
      Math.max(minimumWidth, preview.getBoundingClientRect().width + direction * CAMERA_PREVIEW_SCALE_STEP),
    );
    setPreviewWidth(nextWidth);
    onFloatingLayoutChange?.({ position, width: nextWidth });
  };

  useEffect(() => {
    if (variant !== 'floating') return;
    const keepPreviewInViewport = () => {
      const preview = previewRef.current;
      if (!preview) return;
      const { minimumWidth, maximumWidth } = getPreviewWidthBounds();
      setPreviewWidth((current) => current === null
        ? current
        : Math.min(maximumWidth, Math.max(minimumWidth, current)));
      const rect = preview.getBoundingClientRect();
      setPosition((current) => current
        ? constrainPosition(current, rect.width, rect.height)
        : current);
    };
    keepPreviewInViewport();
    window.addEventListener('resize', keepPreviewInViewport);
    return () => window.removeEventListener('resize', keepPreviewInViewport);
  }, [constrainPosition, previewWidth, status, variant]);

  if (status === 'off' || !robotId) return null;

  return (
    <section
      ref={previewRef}
      className={`robot-camera-preview robot-camera-preview--${variant}${dragging ? ' is-dragging' : ''}`}
      aria-label={`${robotName}のカメラ映像`}
      style={variant === 'floating' ? {
        ...(position ? { left: position.left, top: position.top, right: 'auto', bottom: 'auto' } : {}),
        ...(previewWidth ? { width: previewWidth } : {}),
      } : undefined}
    >
      <RobotCameraMedia
        state={state}
        stream={stream}
        robotName={robotName}
        robotId={robotId}
        variant={variant}
        rotated={rotated}
        defaultAudioMuted={defaultAudioMuted}
        suspendAudio={suspendAudio}
        onAudioMutedChange={onAudioMutedChange}
        onDock={onDock}
        onExpand={onExpand}
        onClose={onClose}
        onRetry={onRetry}
        onPlaying={onPlaying}
        onWaiting={onWaiting}
        onError={onError}
        onStartDragging={variant === 'floating' ? startDragging : undefined}
        onMovePreview={variant === 'floating' ? movePreview : undefined}
        onStopDragging={variant === 'floating' ? stopDragging : undefined}
        onScalePreview={scalePreview}
      />
    </section>
  );
}
